<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { buildAssessmentAnswerPayload } from "#bb/lib/assessmentAnswers";

// 1) Expect 'id' from the parent.
const props = defineProps<{ id: string }>();
const emit = defineEmits<{
  (e: "content-update", value: string): void;
  (e: "answer-error", payload: { id: string; error: unknown }): void;
}>();

// 2) Get the definition data from the content manager (read-only).
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// 3) Inject features (e.g. editor mode).
const features = inject("render-features", { editor: false });

// 4) Define commands for the context menu.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit Textbox",
        description: "Edit the textbox definition",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Textbox Block",
    icon: "heroicons:pencil",
  },
});

// 5) Setup assessment answer binding (instance data only).
const { getAnswer, getCachedAnswer, setAnswer } = useAnswers();

const answer = ref<string>("");
const answerPath = computed(() => getResolvedDataPath(props.id).join("/"));

// Reactively mirror the shared answer cache so an answer made on any surface (the page or the AI
// chat) is reflected here live; getAnswer() ensures the cache has been loaded at least once.
watch(
  () => getCachedAnswer(props.id, answerPath.value)?.value,
  (value) => {
    const next = (value ?? "") as string;
    if (next !== answer.value) {
      answer.value = next;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.id) {
    void getAnswer(props.id, answerPath.value);
  }
});

// 6) Form-field validation wiring (active when nested inside a form container).
const { error, validate } = useFormField({
  id: props.id,
  componentId: ComponentDefinitionIds.TEXT_INPUT,
  getValue: () => answer.value,
  getValidation: () => definitionData.value?.validation,
});

const save = async (newValue: string) => {
  answer.value = newValue;
  if (validate().ok === false || !props.id || !definitionData.value) {
    return;
  }

  try {
    await setAnswer(
      props.id,
      buildAssessmentAnswerPayload({
        block: definitionData.value,
        componentId: ComponentDefinitionIds.TEXT_INPUT,
        value: answer.value,
        path: answerPath.value,
      }),
      { throwOnError: true },
    );
    emit("content-update", answer.value);
  } catch (error) {
    emit("answer-error", { id: props.id, error });
  }
};
</script>

<template>
  <div :class="cn('textbox prose max-w-none dark:prose-invert', $attrs.class ?? '')">
    <!-- Render either an Input or a Textarea based on the definition's 'multiline' flag -->
    <template v-if="definitionData.multiline">
      <Textarea
        :id="props.id"
        :model-value="answer"
        @blur="save($event.target.value)"
        :placeholder="definitionData.placeholder"
        :rows="definitionData.rows"
        :autogrow="definitionData.autogrow"
        :aria-invalid="!!error"
        :class="error ? 'border-destructive focus-visible:ring-destructive' : ''"
      />
    </template>
    <template v-else>
      <Input
        :id="props.id"
        :model-value="answer"
        @blur="save($event.target.value)"
        :placeholder="definitionData.placeholder"
        :aria-invalid="!!error"
        :class="error ? 'border-destructive focus-visible:ring-destructive' : ''"
      />
    </template>
    <p v-if="error" class="text-destructive text-sm mt-1">{{ error }}</p>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds as ComponentDefinitionIdsMetadata } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIdsMetadata.TEXT_INPUT,
  editor: () => import("#bb/components/editor/assessment/text.vue"),
  icon: "lucide:text-cursor-input",
  type: "Textbox Block",
  description: "A simple text input or textarea for the user to enter text",
  default: {
    // No default text value is stored.
    multiline: false,
    placeholder: "Enter your answer",
    rows: 3,
    autogrow: false,
  },
  categories: ["Assessment"],
  order: 10004,
};
</script>
